/**
 * /api/version service worker registration.
 *
 * Registers /api-version-sw.js on app boot so that GET /api/version
 * requests (from any external app via fetch) are served by the SW
 * with the JSON payload from /api/version.json.
 *
 * Generic: any external app (Homeschool, Studio, third-party) that
 * fetches https://freshcards.freshvibeapps.com/api/version gets a
 * JSON response with the same shape as the getFreshcardsVersionInfo()
 * export and the static /version.json file.
 *
 * This is a thin registration helper. The actual SW logic lives in
 * /api-version-sw.js. This file is intentionally minimal so the
 * boot path is obvious and easy to remove if the operator wants to
 * host /api/version on a real backend later.
 *
 * Usage (from src/main.tsx or App.tsx):
 *   import { registerApiVersionServiceWorker } from './version/register-sw';
 *   registerApiVersionServiceWorker();
 *
 * No FreshCards logic changes. Pure registration side-effect.
 */

const SW_URL = '/api-version-sw.js';

let registered = false;

/**
 * Register the /api/version service worker. Idempotent — safe to call
 * multiple times; only the first call has any effect.
 *
 * Returns a Promise that resolves to the ServiceWorkerRegistration
 * on success, or to null if the browser does not support service
 * workers (in which case /api/version will fall back to a normal
 * 404 from nginx — operators can add a rewrite if needed).
 */
export function registerApiVersionServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (registered) return Promise.resolve(null);
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }

  registered = true;
  return navigator.serviceWorker
    .register(SW_URL, { scope: '/' })
    .then((reg) => {
      // eslint-disable-next-line no-console
      console.log('[fc-version] /api/version service worker registered at', SW_URL);
      return reg;
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[fc-version] /api/version service worker registration failed', err);
      return null;
    });
}
